// src/pos-app/surfaces/waiter/lib/tableFlow.js
//
// F12a-N Task W2 — faithful, cut-and-paste extraction of the merge/seat
// business logic from TableServicePage.js, same discipline as the cashier
// surface's lib/ files (see surfaces/cashier/lib/pendingOrderFlow.js
// header). The legacy page is UNTOUCHED and keeps its own inline copies of
// every function below. Only mechanical changes were made: closure-captured
// React state became explicit parameters, bundled into a single `ctx`
// object exactly like the cashier lib's ctx pattern.
//
// Only the multi-step / order-sensitive flows are extracted here — trivial
// single-call handlers (markServed, requestBill, clearTable, split,
// seatWalkIn) stay inline in TablesSurface, same proportionality the
// legacy page itself uses (those were one-liners there too).
//
// Source-range table (legacy TableServicePage.js, current line numbers):
//   seatOnTable                  <- 365-409 (handleSeatOnTable) — the
//                                    seat/change-table banner-mode action,
//                                    branching on seatMode.type. The
//                                    'change_table' branch's order matters:
//                                    occupationService.changeTable THEN
//                                    tenantStorage.changeOrderTable (moves
//                                    the pending-order draft so the bill
//                                    follows the table instead of being
//                                    stranded under the old key).
//   combineMemberOrdersIntoAnchor <- 510-553 — folds each merged member's
//                                    draft order into the anchor's IN
//                                    MEMORY first (products concatenated,
//                                    money fields summed additively), THEN
//                                    saves the combined draft to the anchor
//                                    key, and ONLY THEN deletes the member
//                                    drafts. This ordering is
//                                    loss-prevention-critical: a failure
//                                    between merge and combine must not be
//                                    able to lose a member's items, so
//                                    anchor-save always happens strictly
//                                    before any member-delete.
//   confirmMerge                  <- 555-583 (handleConfirmMerge) — guards
//                                    (>=2 tables, online-only) then
//                                    occupationService.merge FIRST, THEN
//                                    combineMemberOrdersIntoAnchor. Returns
//                                    an error-key object instead of touching
//                                    UI state directly, so this stays
//                                    React-free like the cashier lib.

// Faithful extraction — TableServicePage.js:365-409 (handleSeatOnTable).
// Executes the active seatMode action against `targetTableNumber` (the
// table the waiter tapped while a seat/change-table banner mode was
// active). `ctx.seatMode` carries the mode object set by TablesSurface
// (type: 'walkin' | 'waitlist' | 'reservation' | 'change_table', plus the
// per-type fields the legacy page captured: partySize/guestName for walkin,
// id for waitlist/reservation, occupationId/fromTable for change_table).
export async function seatOnTable(ctx, targetTableNumber) {
    const {
        seatMode, selectedDO, selectedDiningOption, pendingOrders,
        occupationService, waitingListService, reservationService, tenantStorage,
    } = ctx;
    if (!seatMode || !selectedDO) return;

    if (seatMode.type === 'walkin') {
        await occupationService.create({
            dining_option_id: selectedDO.id,
            table_number: targetTableNumber,
            party_size: seatMode.partySize || 2,
            guest_name: seatMode.guestName || null,
        });
    } else if (seatMode.type === 'waitlist') {
        await waitingListService.seat(seatMode.id, {
            dining_option_id: selectedDO.id,
            table_number: targetTableNumber,
        });
    } else if (seatMode.type === 'reservation') {
        await reservationService.seat(seatMode.id, {
            dining_option_id: selectedDO.id,
            table_number: targetTableNumber,
        });
    } else if (seatMode.type === 'change_table') {
        await occupationService.changeTable(seatMode.occupationId, targetTableNumber);
        // Move the order draft too — moving the occupation alone leaves the
        // bill stranded on the old table key (and out of any later merge).
        const oldKey = `${selectedDiningOption}-${seatMode.fromTable}`;
        const movingOrder = pendingOrders[oldKey];
        const movingOrderId = movingOrder && (movingOrder._orderId || movingOrder.order_id);
        if (movingOrderId) {
            const newKey = `${selectedDiningOption}-${targetTableNumber}`;
            await tenantStorage.changeOrderTable(movingOrderId, newKey, selectedDiningOption, targetTableNumber);
        }
    }
}

// Faithful extraction — TableServicePage.js:510-553
// (combineMemberOrdersIntoAnchor). See the header note above for why the
// save-anchor-first-then-delete-members ordering must not be reordered.
export async function combineMemberOrdersIntoAnchor(ctx, anchorTable, memberTables) {
    const { selectedDiningOption, pendingOrders, tenantStorage } = ctx;
    const salesDate = await tenantStorage.getState('salesDate');
    const anchorKey = `${selectedDiningOption}-${anchorTable}`;
    let base = pendingOrders[anchorKey] ? { ...pendingOrders[anchorKey] } : null;
    let anchorOrderId = base ? (base._orderId || base.order_id || null) : null;
    const num = (v) => parseFloat(v) || 0;

    // First fold every member order into `base` in memory (no deletes yet).
    const toDelete = [];
    for (const mt of memberTables) {
        const mKey = `${selectedDiningOption}-${mt}`;
        const mOrder = pendingOrders[mKey];
        if (!mOrder) continue;
        if (!base) {
            // Anchor had no order yet — adopt this member's order as the anchor's.
            base = { ...mOrder };
            anchorOrderId = null; // re-saved fresh under the anchor key
        } else {
            // Order line items live under `products` (not `items`); concatenate
            // both tables' products so the combined bill shows everything.
            base.products = [...(base.products || []), ...(mOrder.products || [])];
            base.subtotal = num(base.subtotal) + num(mOrder.subtotal);
            base.serviceCharge = num(base.serviceCharge) + num(mOrder.serviceCharge);
            base.tax = num(base.tax) + num(mOrder.tax);
            base.discount = num(base.discount) + num(mOrder.discount);
            base.total = num(base.total) + num(mOrder.total);
            base.total_items_count = (base.total_items_count || 0) + (mOrder.total_items_count || 0);
        }
        toDelete.push([mKey, mOrder._orderId || mOrder.order_id || null]);
    }

    if (toDelete.length && base) {
        base.diningOption = selectedDiningOption;
        base.tableNumber = String(anchorTable);
        // Save the combined order to the anchor FIRST; only delete member
        // drafts once it is safely persisted, so a failure can't lose items.
        await tenantStorage.saveOrder(anchorKey, base, false, !!anchorOrderId, anchorOrderId, salesDate);
        for (const [mKey, oid] of toDelete) {
            await tenantStorage.deleteOrder(mKey, oid);
        }
    }
}

// Faithful extraction — TableServicePage.js:555-583 (handleConfirmMerge).
// Returns { error: <t()-key> } on a guard failure, or
// { success: true, anchor, members } once the merge + combine both
// complete — the caller (TablesSurface) owns all UI state (toasts,
// refreshAll, closing merge mode).
export async function confirmMerge(ctx) {
    const { mergeSelection, selectedDO, occupationService, tenantStorage } = ctx;
    if (!mergeSelection || mergeSelection.length < 2) {
        return { error: 'select_two_tables_to_merge' };
    }
    const anchor = String(mergeSelection[0]);
    const members = mergeSelection.slice(1).map(String);

    await occupationService.merge({
        dining_option_id: selectedDO.id,
        anchor_table_number: anchor,
        table_numbers: mergeSelection.map(String),
    });
    await combineMemberOrdersIntoAnchor(ctx, anchor, members);

    return { success: true, anchor, members };
}
